import axios from "axios"
import { NextResponse } from "next/server"
import { z } from "zod"
import { clearSession, getSession } from "@/services/auth-service"
import {
  fetchEditAccountFormSnapshot,
  submitEditAccount,
} from "@/services/wordpress-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const updateSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  displayName: z.string().trim().min(1),
  email: z.string().trim().min(1),
  currentPassword: z.string(),
  newPassword: z.string(),
  confirmNewPassword: z.string(),
})

const getSessionToken = async (): Promise<string | null> => {
  const session = await getSession()
  const token = session?.wordpressSessionCookie?.trim()

  return token || null
}

const toUnauthorizedResponse = () => {
  return NextResponse.json(
    {
      ok: false,
      code: "UNAUTHORIZED",
      message: "Sessao nao autenticada.",
    },
    { status: 401, headers: NO_CACHE_HEADERS }
  )
}

const isUnauthorizedAxiosError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  const status = error.response?.status

  return status === 401 || status === 403
}

export const GET = async () => {
  const sessionToken = await getSessionToken()

  if (!sessionToken) {
    return toUnauthorizedResponse()
  }

  try {
    const snapshot = await fetchEditAccountFormSnapshot(sessionToken)

    return NextResponse.json(
      {
        ok: true,
        details: snapshot.details,
      },
      { headers: NO_CACHE_HEADERS }
    )
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()
      return toUnauthorizedResponse()
    }

    return NextResponse.json(
      {
        ok: false,
        code: "ACCOUNT_DETAILS_FETCH_FAILED",
        message: "Nao foi possivel carregar os detalhes da conta.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

export const POST = async (request: Request) => {
  const sessionToken = await getSessionToken()

  if (!sessionToken) {
    return toUnauthorizedResponse()
  }

  const payload = await request.json().catch(() => null)
  const parsed = updateSchema.safeParse(payload)

  if (!parsed.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para atualizar detalhes da conta.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const snapshot = await fetchEditAccountFormSnapshot(sessionToken)

    if (!snapshot.nonce) {
      return NextResponse.json(
        {
          ok: false,
          code: "NONCE_FETCH_FAILED",
          message: "Nao foi possivel validar a atualizacao da conta.",
        },
        { status: 502, headers: NO_CACHE_HEADERS }
      )
    }

    const formPayload = new URLSearchParams()
    formPayload.set("account_first_name", parsed.data.firstName)
    formPayload.set("account_last_name", parsed.data.lastName)
    formPayload.set("account_display_name", parsed.data.displayName)
    formPayload.set("account_email", parsed.data.email)
    formPayload.set("password_current", parsed.data.currentPassword)
    formPayload.set("password_1", parsed.data.newPassword)
    formPayload.set("password_2", parsed.data.confirmNewPassword)
    formPayload.set("save-account-details-nonce", snapshot.nonce)
    formPayload.set("action", "save_account_details")

    const submitResult = await submitEditAccount(sessionToken, formPayload)

    if (!submitResult.success) {
      return NextResponse.json(
        {
          ok: false,
          code: "WOO_VALIDATION_FAILED",
          message:
            submitResult.notices[0] ??
            "Nao foi possivel atualizar detalhes da conta.",
          errors: submitResult.notices,
        },
        { status: 422, headers: NO_CACHE_HEADERS }
      )
    }

    const refreshedSnapshot = await fetchEditAccountFormSnapshot(sessionToken)

    return NextResponse.json(
      {
        ok: true,
        message:
          submitResult.notices[0] ??
          "Detalhes da conta atualizados com sucesso.",
        details: refreshedSnapshot.details,
      },
      { headers: NO_CACHE_HEADERS }
    )
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()
      return toUnauthorizedResponse()
    }

    return NextResponse.json(
      {
        ok: false,
        code: "ACCOUNT_DETAILS_UPDATE_FAILED",
        message: "Nao foi possivel atualizar detalhes da conta.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
